import { toast } from "@/hooks/use-toast";
// The shared i18next instance (initialised by `@/i18n` at app start). Importing
// `@/i18n` here would pull `react-i18next` into every test that mocks it.
import { registerSuperProperties } from "@/lib/analytics";
import {
  type EntityScopeBlock,
  hasNoSelectableEntity,
  resolveEntityDeepLink,
  resolveEntityScopeBlock,
  resolveRetryInvalidation,
  stripEntityDeepLink,
} from "@/lib/entity-scope-state";
import { isBootstrapQueryKey, markEntityHeaderReady, queryClient } from "@/lib/queryClient";
import type { Entity, Workspace } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import i18n from "i18next";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

interface EntityContextValue {
  entities: Entity[];
  /** Entities the switcher should expose: active by default, plus archived when toggled on. */
  visibleEntities: Entity[];
  activeEntity: Entity | null;
  activeEntityId: string | null;
  setActiveEntityId: (id: string | null) => void;
  isAllEntities: boolean;
  /** True when the active entity is archived. Writes from this state are blocked at the HTTP boundary (E90). */
  isActiveEntityArchived: boolean;
  /** OCT-3366: when true, the switcher reveals archived entities; defaults to false. */
  showArchived: boolean;
  setShowArchived: (value: boolean) => void;
  displayCurrency: string;
  setDisplayCurrency: (currency: string) => void;
  isLoading: boolean;
  /**
   * True when `/api/entities` itself failed. The latch still opens (see the
   * failure-path effect below) so the app does not hang, but no entity
   * header can be resolved, and OCT-10873 made every hardened read answer
   * empty in that state. Surfaced so the shell can say so out loud instead
   * of rendering a plausible-looking empty app.
   */
  entitiesLoadFailed: boolean;
  /**
   * True when the entities list loaded fine but holds nothing selectable -
   * an empty workspace, or one whose every entity is archived. Distinct
   * from `entitiesLoadFailed`: nothing is broken, there is simply nothing
   * to scope to, and empty reads are the truthful answer.
   */
  hasNoSelectableEntity: boolean;
  /** Retry `/api/entities`, dropping the empty caches if it comes back. */
  retryEntitiesLoad: () => void;
  /**
   * True from the moment retry is clicked until the caches have been
   * dropped (or the retry has settled with nothing to drop). The banner
   * disables its button on this, so the affordance matches the fact that a
   * second click while the first is in flight does nothing.
   */
  entitiesRetryPending: boolean;
  /**
   * True once the entity scope is settled enough to make a per-entity
   * request: either a concrete `activeEntity` resolved from the loaded
   * list, or the explicit "All Entities" rollup pick. False while the
   * entities/workspace bootstrap reads are still in flight, and false
   * when nothing selectable was found (`hasNoSelectableEntity`) - in
   * both cases a query gated on this should stay disabled rather than
   * fire with a not-yet-resolved (or unresolvable) entity context.
   *
   * @spec ILLEGAL E89
   */
  isEntityContextResolved: boolean;
}

const EntityContext = createContext<EntityContextValue>({
  entities: [],
  visibleEntities: [],
  activeEntity: null,
  activeEntityId: null,
  setActiveEntityId: () => {},
  isAllEntities: false,
  isActiveEntityArchived: false,
  showArchived: false,
  setShowArchived: () => {},
  displayCurrency: "USD",
  setDisplayCurrency: () => {},
  isLoading: true,
  entitiesLoadFailed: false,
  hasNoSelectableEntity: false,
  retryEntitiesLoad: () => {},
  entitiesRetryPending: false,
  isEntityContextResolved: false,
});

const STORAGE_KEY = "octa-active-entity";
const CURRENCY_STORAGE_KEY = "octa-display-currency";
const SHOW_ARCHIVED_STORAGE_KEY = "octa-show-archived-entities";

/**
 * Should this query be dropped when the entity scope changes (or is retried)?
 *
 * Everything except the app-bootstrap reads. Those are workspace-level and
 * invariant across an entity switch - the auth user, the workspace row, its
 * feature flags and profile config, the entity list itself, billing status and
 * the plan catalogue. Refetching them here buys nothing and costs a burst of
 * requests against the very per-user budget whose exhaustion strands the app
 * shell (OCT-11301).
 *
 * Note the inverse is NOT a safe shortcut: `/api/complexity-dashboard-counts`
 * is a query key, not a route - it fetches `GET /api/dashboard`, which IS
 * entity-scoped, so it must stay in the invalidation set. Excluding it used to
 * leave the dashboard deciding queue-vs-widgets mode from the previous
 * entity's counts for a full staleTime after a switch (OCT-10873).
 */
function isEntityScopeSensitiveQuery(query: { queryKey: readonly unknown[]; }): boolean {
  return !isBootstrapQueryKey(query.queryKey[0]);
}

/**
 * OCT-3366: pick the "first" entity for default landing.
 *
 * Preference order:
 *   1. The workspace's `is_default = true` entity (every workspace has
 *      exactly one per the partial unique index).
 *   2. The first non-archived entity by natural list order (the server
 *      already returns entities sorted by sortOrder asc, isDefault desc,
 *      name asc).
 *
 * Archived entities are never auto-selected. Returns `null` only when the
 * workspace has zero non-archived entities, in which case the UI shows a
 * "Contact your workspace owner" empty state.
 *
 * Exported for unit testing - the EntityProvider's selection effect is
 * the consumer in production.
 */
export function pickDefaultLandingEntity(entities: Entity[]): Entity | null {
  const active = entities.filter((e) => !e.isArchived);
  if (active.length === 0) return null;
  return active.find((e) => e.isDefault) ?? active[0] ?? null;
}

export function EntityProvider({ children }: { children: React.ReactNode; }) {
  const [activeEntityId, setActiveEntityIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [displayCurrency, setDisplayCurrencyState] = useState<string>(() => {
    try {
      return localStorage.getItem(CURRENCY_STORAGE_KEY) || "USD";
    } catch {
      return "USD";
    }
  });

  const [showArchived, setShowArchivedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_ARCHIVED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const {
    data: entities = [],
    isLoading: entitiesLoading,
    isError: entitiesError,
    refetch: refetchEntities,
  } = useQuery<Entity[]>({
    queryKey: ["/api/entities"],
  });

  const { data: workspace, isLoading: workspaceLoading } = useQuery<Workspace>({
    queryKey: ["/api/workspace"],
  });

  const isLoading = entitiesLoading || workspaceLoading;
  const isAllEntities = activeEntityId === "all";

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    try {
      const channel = new BroadcastChannel("octa-entity-sync");
      broadcastChannelRef.current = channel;
      channel.onmessage = (event) => {
        if (event.data?.type === "entity-change" && event.data.entityId !== undefined) {
          setActiveEntityIdState(event.data.entityId);
          queryClient.invalidateQueries({ predicate: isEntityScopeSensitiveQuery });
        }
        if (event.data?.type === "auth-change") {
          // OCT-11301: no `invalidateQueries()` here. The query cache is
          // memory-only (there is no persister), so the reload on the next
          // line discards it wholesale. Invalidating first only fired a
          // refetch of every active query in every open tab, milliseconds
          // before that tab was torn down - pure request volume against the
          // per-user budget, and one of the bursts the 429 storm was built
          // from.
          window.location.reload();
        }
      };
      return () => channel.close();
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue !== activeEntityId) {
        setActiveEntityIdState(event.newValue);
        queryClient.invalidateQueries({ predicate: isEntityScopeSensitiveQuery });
      }
    };
    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [activeEntityId]);

  const setActiveEntityId = useCallback((id: string | null) => {
    setActiveEntityIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    queryClient.invalidateQueries({ predicate: isEntityScopeSensitiveQuery });
    try {
      broadcastChannelRef.current?.postMessage({ type: "entity-change", entityId: id });
    } catch {}
  }, []);

  const setDisplayCurrency = useCallback((currency: string) => {
    setDisplayCurrencyState(currency);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
    } catch {}
  }, []);

  const setShowArchived = useCallback((value: boolean) => {
    setShowArchivedState(value);
    try {
      localStorage.setItem(SHOW_ARCHIVED_STORAGE_KEY, value ? "1" : "0");
    } catch {}
  }, []);

  // OCT-3366: new sessions land on the user's first non-archived entity.
  // "All Entities" rollup is never the default resting state - it must be an
  // explicit user pick from the switcher. If the persisted selection points
  // at an entity that disappeared (e.g. archived since last visit, removed
  // from the workspace), fall back to the same first-non-archived rule.
  //
  // OCT-3337 follow-up: the same effect releases the `queryClient`
  // active-entity validation latch once the persisted LS value has been
  // reconciled (kept, corrected, or confirmed absent). Until the latch
  // opens, non-boot fetches park - so a stale UUID never leaves the
  // browser and the chicken-and-egg with `/api/entities` is broken.
  const entitySigRef = useRef("");
  const validationLatchFiredRef = useRef(false);
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (isLoading || entities.length === 0) return;
    // OCT-15904: an entity-owned link (e.g. a scheduled-report email) names
    // its entity in `?entityId=`. Honour it once, on the first resolution,
    // BEFORE the latch opens so the very first data fetch already carries the
    // linked entity. The parameter is then removed from the address bar so a
    // refresh or a shared URL does not keep forcing the selection.
    if (!deepLinkHandledRef.current) {
      deepLinkHandledRef.current = true;
      const link = resolveEntityDeepLink(window.location.search, entities);
      if (link.kind !== "none") {
        const { pathname, hash } = window.location;
        window.history.replaceState(
          window.history.state,
          "",
          pathname + stripEntityDeepLink(window.location.search) + hash,
        );
      }
      if (link.kind === "switch") {
        if (link.entityId !== activeEntityId) {
          setActiveEntityId(link.entityId);
          if (!validationLatchFiredRef.current) {
            validationLatchFiredRef.current = true;
            markEntityHeaderReady();
          }
          return;
        }
      }
      if (link.kind === "rejected") {
        toast({ title: i18n.t("common:entityDeepLink.noAccess") });
      }
    }
    const sig = entities.map((e) => `${e.id}:${e.isArchived ? "a" : "x"}`).sort().join(",")
      + "|" + (entities.find((e) => e.isDefault)?.id ?? "");
    const matchingPersisted = activeEntityId && activeEntityId !== "all"
      ? entities.find((e) => e.id === activeEntityId)
      : undefined;
    const persistedIsArchived = !!matchingPersisted?.isArchived;
    const needsValidation = !activeEntityId
      || (activeEntityId !== "all" && !matchingPersisted)
      || persistedIsArchived;
    if (entitySigRef.current === sig && !needsValidation) {
      if (!validationLatchFiredRef.current) {
        validationLatchFiredRef.current = true;
        markEntityHeaderReady();
      }
      return;
    }
    entitySigRef.current = sig;
    if (!activeEntityId || persistedIsArchived) {
      const landing = pickDefaultLandingEntity(entities);
      if (landing) setActiveEntityId(landing.id);
      if (!validationLatchFiredRef.current) {
        validationLatchFiredRef.current = true;
        markEntityHeaderReady();
      }
      return;
    }
    if (activeEntityId !== "all" && !matchingPersisted) {
      const landing = pickDefaultLandingEntity(entities);
      if (landing) setActiveEntityId(landing.id);
    }
    if (!validationLatchFiredRef.current) {
      validationLatchFiredRef.current = true;
      markEntityHeaderReady();
    }
  }, [entities, isLoading, activeEntityId, setActiveEntityId]);

  // OCT-3337 follow-up: failure path - release the latch if the entities
  // query errored or the workspace has zero entities. Without this, every
  // non-boot fetch would await the latch forever and the app would show
  // skeletons indefinitely.
  //
  // OCT-10873: releasing it is still right, but it is no longer harmless.
  // The latch opens with no entity resolved, so every hardened read now
  // answers an empty 200 instead of widening to the workspace - and
  // `staleTime: Infinity` keeps that empty answer for the rest of the
  // session while a 4xx on `/api/entities` is deliberately not retried.
  // The blank app that results is indistinguishable from a genuinely empty
  // one, so the state is published on the context below and the shell
  // renders `EntityScopeBanner` over it with a retry. Failing closed is
  // correct; failing closed *silently* is what this stops.
  useEffect(() => {
    if (validationLatchFiredRef.current) return;
    if (isLoading) return;
    if (entitiesError || entities.length === 0) {
      validationLatchFiredRef.current = true;
      markEntityHeaderReady();
    }
  }, [entitiesError, entities.length, isLoading]);

  const prevEntityRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (prevEntityRef.current === activeEntityId) return;
    prevEntityRef.current = activeEntityId;
    let resolvedCurrency = displayCurrency;
    if (isAllEntities) {
      resolvedCurrency = workspace?.defaultCurrency || "USD";
      setDisplayCurrency(resolvedCurrency);
    } else {
      const entity = entities.find((e) => e.id === activeEntityId);
      if (entity?.currency) {
        resolvedCurrency = entity.currency;
        setDisplayCurrency(resolvedCurrency);
      }
    }
    registerSuperProperties({ active_entity_id: activeEntityId, display_currency: resolvedCurrency });
  }, [activeEntityId, isAllEntities, entities, workspace, isLoading]);

  const activeEntity = isAllEntities ? null : (entities.find((e) => e.id === activeEntityId) || null);
  const isActiveEntityArchived = !!activeEntity?.isArchived;
  // OCT-12899: explicit "the entity scope is settled" signal for callers
  // (e.g. the Communications Hub) that must not fire a per-entity query
  // until a concrete entity - or the explicit rollup - has resolved.
  const isEntityContextResolved = !isLoading && !entitiesError && (isAllEntities || !!activeEntity);
  // OCT-3366: by default the switcher shows only active entities. When the
  // operator flips "Show archived" on, archived entities appear in the list
  // (selecting one is allowed but the active-entity middleware refuses to
  // resolve the header for archived ids, so the UI surfaces a read-only
  // affordance once the selection actually lands).
  const visibleEntities = showArchived ? entities : entities.filter((e) => !e.isArchived);
  // Nothing to scope to: an empty workspace, or one where every entity is
  // archived so `pickDefaultLandingEntity` can never land.
  const noSelectableEntity = hasNoSelectableEntity(entities, {
    isLoading,
    loadFailed: entitiesError,
  });

  // Retrying has to drop the empty-result caches as well as refetch, or the
  // lists stay blank on `staleTime: Infinity` even after the entity
  // resolves.
  //
  // OCT-11301: scoped, not everything. This button is the one affordance a
  // user has when a throttled `/api/entities` has emptied the whole app, so
  // it is precisely the moment when refetching EVERY active query - including
  // the five bootstrap reads that were just throttled, and `/api/entities`
  // itself, which `refetchEntities()` above has already re-run - walks
  // straight back into the 429 the user is trying to escape. Only the
  // entity-scoped caches need dropping; those are what answered empty.
  //
  // It deliberately does NOT invalidate inside the refetch promise.
  // `getActiveEntityHeader` reads the entity out of localStorage, which the
  // selection effect above writes on a *later* render, so invalidating in
  // the callback refetches every list while the header is still missing and
  // re-pins the same empty answer. The flag below waits for a scope to
  // resolve first; `resolveRetryInvalidation` holds the rule and is unit
  // tested.
  const [retryPending, setRetryPending] = useState(false);
  const [refetchInFlight, setRefetchInFlight] = useState(false);
  // A ref rather than the state above, because the state cannot reject a
  // second click: both clicks in one frame run the same render's closure and
  // both read `false`. React Query does dedupe the request itself, but the
  // click also arms `retryPending`, and arming it twice re-runs the
  // invalidation after the first one has already settled.
  const refetchInFlightRef = useRef(false);
  const retryEntitiesLoad = useCallback(() => {
    if (refetchInFlightRef.current) return;
    refetchInFlightRef.current = true;
    setRefetchInFlight(true);
    void refetchEntities()
      .then((result) => {
        if (!result.error) setRetryPending(true);
      })
      .finally(() => {
        // `finally`, not the success branch: a rejection here would otherwise
        // leave the ref armed and wedge the button disabled for the session.
        refetchInFlightRef.current = false;
        setRefetchInFlight(false);
      });
  }, [refetchEntities]);
  useEffect(() => {
    const next = resolveRetryInvalidation({
      pending: retryPending,
      isLoading,
      hasResolvedScope: isAllEntities || !!activeEntity,
      noSelectableEntity,
      loadFailed: entitiesError,
    });
    if (next === "wait") return;
    setRetryPending(false);
    if (next === "invalidate") {
      queryClient.invalidateQueries({ predicate: isEntityScopeSensitiveQuery });
    }
  }, [retryPending, isLoading, isAllEntities, activeEntity, noSelectableEntity, entitiesError]);

  return (
    <EntityContext.Provider
      value={{
        entities,
        visibleEntities,
        activeEntity,
        activeEntityId,
        setActiveEntityId,
        isAllEntities,
        isActiveEntityArchived,
        showArchived,
        setShowArchived,
        displayCurrency,
        setDisplayCurrency,
        isLoading,
        entitiesLoadFailed: entitiesError,
        hasNoSelectableEntity: noSelectableEntity,
        retryEntitiesLoad,
        entitiesRetryPending: refetchInFlight || retryPending,
        isEntityContextResolved,
      }}
    >
      {children}
    </EntityContext.Provider>
  );
}

export function useActiveEntity() {
  return useContext(EntityContext);
}

/**
 * OCT-3366: tiny hook for write-form / create-button gating on list pages.
 *
 * Returns `{ blocked, reason }`:
 * - `blocked = true` when the active scope is "All Entities" rollup, the
 *   active entity is archived, or no concrete entity is resolved at all
 *   (`absent`). In all three the server would refuse the write (E89 + rollup
 *   sentinel guard, E90 archive read-only, unresolvable header). Surfacing
 *   the block client-side avoids the round-trip and keeps the UX honest.
 * - `reason` carries the canonical i18n key callers should pass to `t(...)`
 *   when rendering the disabled-state tooltip / empty-state copy.
 *
 * Callers wire this up by:
 *   1. Disabling the create CTA (`<Button disabled={blocked} ...>`).
 *   2. Wrapping it in a `<Tooltip>` whose content uses the returned reason.
 *   3. Optionally swapping the list empty-state to the same reason.
 *
 * The hook intentionally does not throw or navigate - it just reports state;
 * the caller decides the affordance. Pixel polish lives in each page's CTA
 * cluster; the contract here is "block server-rejectable writes locally".
 */
export function useRollupWriteGuard(): EntityScopeBlock {
  const { isAllEntities, isActiveEntityArchived, activeEntity, isLoading } = useActiveEntity();
  return resolveEntityScopeBlock({
    isAllEntities,
    isActiveEntityArchived,
    hasActiveEntity: !!activeEntity,
    isLoading,
  });
}
